import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NormeSpaMonisticoFormValidators } from 'app/features/revisione53/core/forms/amm-controllo/norme-spa-monistico/validators/norme-spa-monistico.form-validators';
import { NormeSpaMonisticoWarningsService } from './warnings/norme-spa-monistico.warnings';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import {
    buildComitatoControlloGestioneFormArray,
    buildConsiglioAmministrazioneFormArray,
    buildLiquidatoriFormArray,
    buildOrganoControlloFormArray,
    buildRevisoreFormArray,
    clearNormeFormArrays,
} from '../common/helpers/norme-form-groups.helper';
import { normalizeArraysNorme } from '../common/helpers/norme-normalize.helper';
import { cloneDeep } from 'lodash-es';
import { CkNormeValuesEnum } from '../common/consts/ckNormeValues.enum';
import { NormeSpaMonisticoFormListeners } from './listeners/norme-spa-monistico.form-listeners';
import { BaseRevisioneFormService } from '../../../abstracts/base-revisione-form.service';
import { formPaths } from '@shared/constants/form-paths.const';
import { getControl } from '@shared/helpers/form.helper';
import { controlloCheckComitatoControllo } from '../common/helpers/norme-spa-helpers.helper';

@Injectable()
export class NormeSpaMonisticoFormService extends BaseRevisioneFormService<NormeSpaMonisticoWarningsService> {
    constructor(protected warningService: NormeSpaMonisticoWarningsService) {
        super(warningService);
    }

    override initListener(): void {
        new NormeSpaMonisticoFormListeners(this._form, this.store).setListeners();
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Stato pratica
            'my_IdAssegnazione',

            // Global fields
            'my_GlobalFields.my_StampaMinistero_ChkSRL',
            'my_GlobalFields.my_StampaMinistero_ChkSPO',
            'my_GlobalFields.my_StampaMinistero_ChkSPD',
            'my_GlobalFields.my_StampaMinistero_ChkSPM',

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_BilAnno1`,
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT2`,

            // Raggiungimento scopo sociale
            `${formPaths.scopoSociale}.my_RicaviVendAnno1Dep`,
            `${formPaths.scopoSociale}.my_RicaviVendDep`,

            // Effettività base sociale
            `${formPaths.baseSociale}.my_SociFisCoopData`,
            `${formPaths.baseSociale}.my_SociGiuCoopData`,
            `${formPaths.baseSociale}.my_SociSpecData`,
            `${formPaths.baseSociale}.my_ULAPrimo`,
            `${formPaths.baseSociale}.my_ULASecondo`,

            // Conclusioni
            `${formPaths.conclusioni}.my_DataCompilaz`,
        ]);
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_IdAssegnazione: '',
                my_GlobalFields: this.fb.group({
                    my_StampaMinistero_ChkSRL: '',
                    my_StampaMinistero_ChkSPO: '',
                    my_StampaMinistero_ChkSPD: '',
                    my_StampaMinistero_ChkSPM: '',
                }),
                my_SezioneI__Rilevazione: this.fb.group({
                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                    }),

                    my_Bilancio: this.fb.group({
                        my_CkEnteControllaSocObbRevCont: '',
                        my_CkRedazBilConsol: '',
                    }),

                    my_EffettivitaBaseSociale: this.fb.group({
                        my_SociFisCoopData: '',
                        my_SociGiuCoopData: '',
                        my_SociSpecData: '',
                        my_ULAPrimo: '',
                        my_ULASecondo: '',
                    }),

                    my_RaggScopoSociale: this.fb.group({
                        my_RicaviVendAnno1Dep: '',
                        my_RicaviVendDep: '',
                    }),

                    my_Conclusioni: this.fb.group({
                        my_DataCompilaz: '',
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_BilAnno1: '',
                        my_BilAnno2: '',
                        my_StatoPatrimoniale__Attivo: this.fb.group({
                            my_StPaAtTOT1: '',
                            my_StPaAtTOT2: '',
                        }),
                    }),

                    my_AmministrazioneControllo: this.fb.group({
                        my_CkNorme: '',
                        my_CalcObbOrganoContr: '',
                        my_CkLiquidatoriRelazionano: '',
                        my_CriteriRelazioneLiquidatori: '',
                        my_CkSindaciRiunioniLiquidatori: '',
                        my_CkScadenzaOrgAmm: '',
                        my_CkRegistroDelibLiquidatori: '',
                        my_DataAssembleaLiquidazVolontaria: '',
                        my_ControlloRevLegali: '',
                        my_ControlloSoci: '',
                        my_NoteAttivitaLiquidatori: '',
                        my_CkOrgSocietCorrCostit: '',
                        my_CkOrgSocietCorrFunz: '',
                        my_NoteEdOsservazioni: '',
                        my_ControlloRevisoreContabileSocio: '',
                        my_NumLiquidatori: '',
                        my_AllLiquidatori: this.fb.group({
                            my_AllElemLiquidatori: this.fb.array([]),
                        }),

                        my_AllRevisoriControlloContabile: this.fb.group({
                            my_AllElemRevisoriControlloContabile: this.fb.array([]),
                        }),

                        my_SocRevisioneControlloContabile: this.fb.group({
                            my_Id_SocPrecaricata: '',
                            my_RagSocSocRevisione: '',
                            my_DataAffIncarico: '',
                        }),

                        my_AllConsiglioAmministrazione: this.fb.group({
                            my_AllElemConsiglioAmministrazione: this.fb.array([]),
                        }),

                        my_AllCollegioSindacale: this.fb.group({
                            my_AllElemCollegioSindacale: this.fb.array([]),
                        }),

                        my_NormeSpaMonistico: this.fb.group({
                            my_lblSpmContrCont: '',
                            my_lblSpmLiquidatori: '',
                            my_lblSpmConsAmm: '',
                            my_CkSpmCoop9soci: [{ value: '', disabled: true }, null, null],
                            my_CkSpmGratuitaCariche: '',
                            my_CkSpmAssembDetCompAmmin: '',
                            my_CkSpmAssembAppBil: '',
                            my_CkSpmAssembNominAmmin: '',
                            my_CkSpmAssembNominOrgano: '',
                            my_SpmNoteFunzAssemb: '',
                            my_CkSpmTipoAmministrazione: '',
                            my_SpmScadenzaAmministratori: '',
                            my_CkSpmMaggAmminSoci: '',
                            my_CkSpmMetaAmminIndip: '',
                            my_CkSpmAmminCarattMutual: '',
                            my_CkSpmCompStatoInConsAmmin: '',
                            my_SpmNoteComposConsAmmin: '',

                            my_SpmComitatoGestione: this.fb.group({
                                my_SpmElemComitatoGestione: this.fb.array([]),
                            }),

                            my_CkSpmComitGestConsAmmin: '',
                            my_CkSpmCompStatoInOrgContr: '',
                            my_CkSpmCompRevisori: '',
                            my_CkSpmCompOrgContrEletti: '',
                            my_CkSpmCompRelazionano: '',
                            my_SpmNoteComposComitGest: '',
                            my_CkSpmAffidamContrCont: '',
                            my_SpmNoteComposOrgContr: '',
                        }),
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: NormeSpaMonisticoFormValidators,
            }
        );
    }

    protected override beforePopulateForm(myVerbale: MyVerbale): void {
        const v = normalizeArraysNorme(cloneDeep(myVerbale));
        this.patchAll(v);
    }

    private lastCheckVerbaleId: string | null = null;
    private lastCheckMemberCount: number | null = null;

    protected override afterPopulateForm(myVerbale: MyVerbale): void {
        // REM-42: il VBS ricalcolava i contatori my_ControlloRevLegali/my_ControlloSoci dell'organo
        // di controllo (per SPM il Comitato Controllo Gestione) in XDocument_OnLoad. I listener NCR
        // reagiscono solo al cambio membri, quindi il primo calcolo va forzato al load. Si ripete anche
        // a parità di verbale se nel frattempo sono arrivati i membri (caricamento asincrono): senza il
        // controllo sul conteggio, un primo giro con array ancora vuoto blocca il ricalcolo per il resto
        // della sessione.
        const idAssegnazione = myVerbale?.my_IdAssegnazione ?? null;
        if (myVerbale?.my_SezioneI__Rilevazione?.my_AmministrazioneControllo?.my_CkNorme !== CkNormeValuesEnum.normeSpaMonistico) return;
        const componentiComitatoGestione = getControl(this._form, `${formPaths.ammControllo}.my_NormeSpaMonistico.my_SpmComitatoGestione.my_SpmElemComitatoGestione`)?.value ?? [];
        const memberCount = componentiComitatoGestione.length;
        if (this.lastCheckVerbaleId === idAssegnazione && this.lastCheckMemberCount === memberCount) return;
        this.lastCheckVerbaleId = idAssegnazione;
        this.lastCheckMemberCount = memberCount;
        controlloCheckComitatoControllo(this._form, this.store, componentiComitatoGestione);
    }

    private patchAll(my_Verbale: MyVerbale): void {
        if (my_Verbale?.my_SezioneI__Rilevazione?.my_AmministrazioneControllo?.my_CkNorme === CkNormeValuesEnum.normeSpaMonistico) {
            buildOrganoControlloFormArray(this.fb, my_Verbale, this._form);
            buildLiquidatoriFormArray(this.fb, my_Verbale, this._form);
            buildConsiglioAmministrazioneFormArray(this.fb, my_Verbale, this._form);
            buildRevisoreFormArray(this.fb, my_Verbale, this._form);
            buildComitatoControlloGestioneFormArray(this.fb, my_Verbale, this._form);
        } else {
            // Verbale di norma diversa: svuota gli array per evitare residui dal verbale precedente.
            clearNormeFormArrays(this._form);
        }
    }
}
